import React, { useState, useEffect } from 'react';
import { accountsAPI, checkDepositsAPI } from '../services/api';
import { formatCurrency } from '../utils/currencyFormatter';

const Deposit = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [frontImagePreview, setFrontImagePreview] = useState(null);
  const [backImagePreview, setBackImagePreview] = useState(null);
  const [frontImageBase64, setFrontImageBase64] = useState(null);
  const [backImageBase64, setBackImageBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadAccounts();
    loadDeposits();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await accountsAPI.getAccounts();
      // API returns { status, data: { accounts: [...] } }
      const apiAccounts = response?.data?.accounts || response?.data || [];
      setAccounts(apiAccounts);
      if (apiAccounts.length > 0) {
        setSelectedAccountId(apiAccounts[0].id);
      }
    } catch (e) {
      console.error('Failed to load accounts for deposit:', e);
      setError('Unable to load your accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDeposits = async () => {
    try {
      const response = await checkDepositsAPI.getDeposits({ per_page: 10 });
      // Backend returns a paginator; normalize to an array
      const items = response?.data?.data || response?.data || [];
      setDeposits(items);
    } catch (e) {
      console.error('Failed to load check deposits:', e);
    }
  };

  const handleFileChange = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      if (side === 'front') {
        setFrontImagePreview(result);
        setFrontImageBase64(result);
      } else {
        setBackImagePreview(result);
        setBackImageBase64(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedAccountId) {
      setError('Please select an account.');
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (!frontImageBase64 || !backImageBase64) {
      setError('Please upload both front and back images of the check.');
      return;
    }

    try {
      setSubmitting(true);
      await checkDepositsAPI.createDeposit({
        account_id: selectedAccountId,
        amount: Number(amount),
        check_number: checkNumber || null,
        front_image: frontImageBase64,
        back_image: backImageBase64,
      });

      setSuccess('Your check has been submitted and is pending review.');
      setAmount('');
      setCheckNumber('');
      setFrontImagePreview(null);
      setBackImagePreview(null);
      setFrontImageBase64(null);
      setBackImageBase64(null);
      await loadDeposits();
    } catch (e) {
      console.error('Check deposit submission failed:', e);
      const message =
        e?.response?.data?.message ||
        'Failed to submit check deposit. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'pending':
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Mobile Check Deposit</h1>
          <p className="mt-2 text-sm text-gray-600">
            Deposit checks securely using your device camera. Funds will be available after review and approval.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Left: Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">New Deposit</h2>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-700">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deposit to Account
                </label>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  {accounts.map((acct) => (
                    <option key={acct.id} value={acct.id}>
                      {acct.name || acct.account_name || 'Account'} • {acct.account_number}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Check Number (optional)
                  </label>
                  <input
                    type="text"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g. 1042"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Front of Check
                  </label>
                  <div className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    {frontImagePreview ? (
                      <img
                        src={frontImagePreview}
                        alt="Front of check"
                        className="max-h-32 rounded-md object-contain"
                      />
                    ) : (
                      <p className="text-xs text-gray-500 text-center">
                        Upload a clear photo of the front of your check
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'front')}
                      className="mt-2 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Back of Check
                  </label>
                  <div className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    {backImagePreview ? (
                      <img
                        src={backImagePreview}
                        alt="Back of check"
                        className="max-h-32 rounded-md object-contain"
                      />
                    ) : (
                      <p className="text-xs text-gray-500 text-center">
                        Upload a clear photo of the back of your check with endorsement
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'back')}
                      className="mt-2 text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Submit Deposit'}
                </button>
              </div>
            </form>
          </div>

          {/* Right: Recent deposits */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Deposits</h2>
            {deposits.length === 0 ? (
              <p className="text-sm text-gray-500">
                You have no check deposits yet. Submitted deposits will appear here.
              </p>
            ) : (
              <ul className="space-y-3 text-sm">
                {deposits.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-start justify-between rounded-lg border border-gray-100 px-3 py-2"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatCurrency(parseFloat(d.amount || 0) || 0)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {d.deposit_id} • {d.account?.account_number || 'Account'}
                      </p>
                      {d.rejection_reason && (
                        <p className="mt-1 text-xs text-red-600">
                          Reason: {d.rejection_reason}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      {d.front_image_url && (
                        <img
                          src={d.front_image_url}
                          alt="Front of check"
                          className="max-h-10 rounded border border-gray-200 object-contain"
                        />
                      )}
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          d.status
                        )}`}
                      >
                        {d.status || 'pending'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deposit;


